import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Business, Service, OperatingHours, BusinessBranding, ServiceReview, Appointment } from '../../types';
import { useBookingAuth } from '../../hooks/useBookingAuth';
import { useAuth } from '../../hooks/useAuth';
import { apiService } from '../../services/api';
import UserProfileDropdown from '../UserProfileDropdown';
import ServiceIcon from '../ServiceIcon';
import ContactForm from '../ContactForm';
import BusinessHeader from '../BusinessHeader';
import BusinessFooter from '../BusinessFooter';
import TenantAskAiWidget from '../TenantAskAiWidget';
import { Clock, List, Tag, ChevronDown, Search, X, Heart, ChevronLeft, ChevronRight, Camera, Maximize2, Store, MapPin } from 'lucide-react';
import toast from 'react-hot-toast';
import { searchServices } from '../../utils/searchUtils';
import { filterCustomerBookableServices } from '../../utils/serviceUtils';

interface UnifiedLayoutProps {
  business: Business;
  services: Service[];
  operatingHours: OperatingHours[];
  branding?: BusinessBranding;
  serviceReviews: ServiceReview[];
  categories?: Array<{ id: string; name: string; icon?: string; display_order?: number }>;
  formatTime: (time: string) => string;
  getDayName: (dayOfWeek: number) => string;
  formatPrice: (price: number) => string;
  formatDuration: (minutes: number) => string;
}

const UnifiedLayout: React.FC<UnifiedLayoutProps> = ({
  business,
  services,
  operatingHours,
  branding,
  serviceReviews,
  categories: providedCategories,
  formatTime,
  getDayName,
  formatPrice,
  formatDuration
}) => {
  const bookableServices = React.useMemo(
    () => filterCustomerBookableServices(services),
    [services]
  );
  const { handleBookingClick, handleServiceBookingClick } = useBookingAuth(business.shortName);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [hasUpcomingAppointments, setHasUpcomingAppointments] = useState(false);
  const [checkingAppointments, setCheckingAppointments] = useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [favoriteServiceIds, setFavoriteServiceIds] = useState<Set<string>>(new Set());
  const [loadingFavorites, setLoadingFavorites] = useState<boolean>(!!user); // Start as true if user exists
  const [hasInitializedCategory, setHasInitializedCategory] = useState<boolean>(false);
  const [mobileServicesDisplayCount, setMobileServicesDisplayCount] = useState<number>(7);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState<number>(0);
  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState<boolean>(false);
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [loadingGalleryImages, setLoadingGalleryImages] = useState<boolean>(false);
  const [isThumbnailDragging, setIsThumbnailDragging] = useState<boolean>(false);
  const [thumbnailStartX, setThumbnailStartX] = useState<number>(0);
  const [thumbnailScrollLeft, setThumbnailScrollLeft] = useState<number>(0);
  const [thumbnailDragDistance, setThumbnailDragDistance] = useState<number>(0);
  const [shouldCenterThumbnails, setShouldCenterThumbnails] = useState<boolean>(false);
  const thumbnailContainerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [todayClosures, setTodayClosures] = useState<any[]>([]);

  // Fetch gallery images asynchronously
  useEffect(() => {
    const fetchGalleryImages = async () => {
      if (!business?.shortName) return;
      
      try {
        setLoadingGalleryImages(true);
        const galleryData = await apiService.getGalleryImages(business.shortName);
        const apiBaseUrl = process.env.REACT_APP_API_URL || (process.env.REACT_APP_ENV === 'production' ? 'https://booking-i73o.onrender.com/api' : 'http://localhost:3001/api');
        const imageUrls = galleryData.map(img => {
          const fullUrl = img.url.startsWith('http') ? img.url : `${apiBaseUrl}${img.url}`;
          return fullUrl;
        });
        setGalleryImages(imageUrls);
      } catch (error) {
        console.error('Error fetching gallery images:', error);
        setGalleryImages([]);
      } finally {
        setLoadingGalleryImages(false);
      }
    };

    fetchGalleryImages();
  }, [business?.shortName]);

  // Check if thumbnails should be centered (only if no horizontal scrollbar)
  const checkAndCenterThumbnails = useCallback(() => {
    if (galleryImages.length > 0 && thumbnailContainerRef.current) {
      const container = thumbnailContainerRef.current;
      
      // Wait a bit for any pending layout calculations
      requestAnimationFrame(() => {
        if (thumbnailContainerRef.current) {
          const container = thumbnailContainerRef.current;
          
          // Check if there's a horizontal scrollbar (content overflows)
          // Add a small tolerance (2px) to account for rounding differences and borders
          const hasHorizontalScroll = container.scrollWidth > container.clientWidth + 2;
          
          // Only center if there's no horizontal scrollbar (all thumbnails fit on screen)
          setShouldCenterThumbnails(!hasHorizontalScroll);
        }
      });
    } else {
      setShouldCenterThumbnails(false);
    }
  }, [galleryImages.length]);

  // Center thumbnails on initial load only if there's no horizontal scrollbar
  useEffect(() => {
    if (galleryImages.length > 0) {
      // Use setTimeout to ensure DOM is fully rendered and images are loaded
      const timer = setTimeout(() => {
        checkAndCenterThumbnails();
      }, 200); // Delay to ensure layout is complete
      
      return () => clearTimeout(timer);
    } else {
      setShouldCenterThumbnails(false);
    }
  }, [galleryImages.length, checkAndCenterThumbnails]);

  // Recheck on window resize
  useEffect(() => {
    const handleResize = () => {
      checkAndCenterThumbnails();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [checkAndCenterThumbnails]);

  // Auto-rotate gallery images (only when modal is closed)
  useEffect(() => {
    if (galleryImages.length <= 1 || isGalleryModalOpen) return;

    const interval = setInterval(() => {
      setCurrentGalleryIndex((prev) => 
        prev === galleryImages.length - 1 ? 0 : prev + 1
      );
    }, 5000); // Rotate every 5 seconds

    return () => clearInterval(interval);
  }, [galleryImages.length, isGalleryModalOpen]);

  // Extract colors from branding, with defaults matching the HTML
  const brandColor = branding?.primaryColor || '#e11d48';
  const brandDark = branding?.secondaryColor || '#9f1239';
  const brandSoft = branding?.accentColor ? `${branding.accentColor}15` : '#fff1f2';
  const bgColor = branding?.backgroundColor || '#ffeef0';
  const surfaceColor = '#ffffff';
  const borderColor = branding?.accentColor ? `${branding.accentColor}40` : '#f5c2cc';
  const textColor = branding?.textColor || '#111827';
  const mutedColor = branding?.textColor ? `${branding.textColor}80` : '#6b7280';

  // Extract unique categories similar to BookingStep1
  const getUniqueCategories = useCallback(() => {
    interface Cat { id: string; name: string; displayOrder: number; icon?: string; }
    const ORDER_FALLBACK = 999999;
    
    // Create a map of provided categories for quick lookup (exactly like BookingStep1)
    const categoryMap = new Map<string, { icon?: string; display_order?: number }>();
    if (providedCategories) {
      providedCategories.forEach(cat => {
        categoryMap.set(cat.id, { icon: cat.icon, display_order: cat.display_order });
      });
    }
    
    const categories = bookableServices.reduce((acc, svc) => {
      const s: any = svc;
      let catObj: any = undefined;
      if (s.category) catObj = s.category;
      else if (s.categoryId && s.categoryName) catObj = { id: s.categoryId, name: s.categoryName, displayOrder: s.categoryDisplayOrder, icon: s.categoryIcon };
      else if (s.category_id && s.category_name) catObj = { id: s.category_id, name: s.category_name, display_order: s.category_display_order, icon: s.category_icon };
      else if (typeof s.category === 'string') catObj = { id: s.category, name: s.category };

      if (!catObj) return acc;

      const id = catObj.id || catObj.category_id || catObj.categoryId;
      const name = catObj.name || catObj.category_name || catObj.categoryName || 'Unnamed';
      if (!id || !name) return acc;

      const rawOrder = catObj.displayOrder ?? catObj.display_order ?? catObj.displayorder ?? catObj.order ?? catObj.sequence ?? catObj.sortOrder;
      const displayOrder = Number.isFinite(Number(rawOrder)) ? Number(rawOrder) : ORDER_FALLBACK;
      
      // Get icon from provided categories if available, otherwise from service data (exactly like BookingStep1)
      const categoryInfo = categoryMap.get(id.toString());
      const icon = categoryInfo?.icon || catObj.icon || catObj.category_icon || catObj.categoryIcon;
      
      // Use display_order from provided categories if available
      const finalDisplayOrder = categoryInfo?.display_order !== undefined 
        ? Number(categoryInfo.display_order) 
        : displayOrder;

      if (!acc.some(c => c.id === id)) {
        acc.push({ id: id.toString(), name: String(name), displayOrder: finalDisplayOrder, icon });
      }
      return acc;
    }, [] as Cat[]);
    const sorted = categories.sort((a, b) => (a.displayOrder - b.displayOrder) || a.name.localeCompare(b.name));
    return sorted;
  }, [bookableServices, providedCategories]);
  
  const categories = getUniqueCategories();
  
  // Helper function to get service icon with fallback to category icon
  const getServiceIcon = useCallback((service: Service): string | null => {
    const s: any = service;
    
    // 1. If service has an icon, use it
    const serviceIcon = s.icon || service.icon;
    if (serviceIcon && typeof serviceIcon === 'string' && serviceIcon.trim() !== '') {
      return serviceIcon;
    }

    // 2. If no service icon, try to get category icon
    const cat: any = s.category;
    const categoryId = cat?.id || cat?.category_id || cat?.categoryId || s.categoryId || s.category_id;
    
    // Try to get icon from category object on service
    const categoryIcon = cat?.icon || cat?.category_icon || cat?.categoryIcon || s.categoryIcon || s.category_icon;
    if (categoryIcon && typeof categoryIcon === 'string' && categoryIcon.trim() !== '') {
      return categoryIcon;
    }

    // Try to get icon from provided categories list
    if (categoryId && providedCategories?.length) {
      const match = providedCategories.find(c => c.id.toString() === categoryId.toString());
      if (match?.icon && typeof match.icon === 'string' && match.icon.trim() !== '') {
        return match.icon;
      }
    }

    // Try to get icon from categories list (extracted from services)
    if (categoryId) {
      const match = categories.find(c => c.id.toString() === categoryId.toString());
      if (match?.icon && typeof match.icon === 'string' && match.icon.trim() !== '') {
        return match.icon;
      }
    }

    // 3. No icon found, return null (will use star icon as fallback)
    return null;
  }, [categories, providedCategories]);
  
  // Add "My Favorites" category if user has favorites
  const hasFavorites = favoriteServiceIds.size > 0;
  const categoriesWithFavorites = hasFavorites 
    ? [
        { id: 'all', name: 'All Services', displayOrder: -1, icon: undefined },
        { id: 'my-favorites', name: 'My Favorites', displayOrder: 0, icon: '❤️' },
        ...categories
      ]
    : [
        { id: 'all', name: 'All Services', displayOrder: -1, icon: undefined },
        ...categories
      ];
  
  // Set default category selection: "My Favorites" if exists, otherwise first category after "All Services"
  useEffect(() => {
    // Only set default when favorites have finished loading and we haven't initialized yet
    // Also check if we're still on 'all' to avoid overriding user selection
    if (!loadingFavorites && !hasInitializedCategory && selectedCategory === 'all' && categoriesWithFavorites.length > 1) {
      if (hasFavorites) {
        setSelectedCategory('my-favorites');
      } else {
        // Select first category after "All Services"
        setSelectedCategory(categoriesWithFavorites[1].id);
      }
      setHasInitializedCategory(true);
    }
  }, [loadingFavorites, hasFavorites, categoriesWithFavorites.length, hasInitializedCategory, selectedCategory]);

  // Auto-switch to "All Services" if "My Favorites" is selected but there are no favorites
  useEffect(() => {
    if (selectedCategory === 'my-favorites' && favoriteServiceIds.size === 0 && !loadingFavorites) {
      setSelectedCategory('all');
    }
  }, [selectedCategory, favoriteServiceIds.size, loadingFavorites]);

  // Reset mobile services display count when category changes
  useEffect(() => {
    setMobileServicesDisplayCount(7);
  }, [selectedCategory, searchQuery]);
  
  // First filter by category
  let categoryFiltered = bookableServices;
  if (selectedCategory === 'my-favorites') {
    categoryFiltered = bookableServices.filter(service => favoriteServiceIds.has(service.id));
  } else if (selectedCategory !== 'all') {
    categoryFiltered = bookableServices.filter(service => {
      const matchesCategory = service.category?.id === selectedCategory ||
        service.category?.name === selectedCategory;
      return matchesCategory;
    });
  }
  
  // Apply fuzzy search if query exists
  let filtered = categoryFiltered;
  if (searchQuery && searchQuery.trim()) {
    filtered = searchServices(categoryFiltered, searchQuery);
  }
  
  // Sort: add-ons go to the end
  const filteredServices = filtered.sort((a, b) => {
    const aIsAddOn = (a as any).isAddOn || (a as any).is_add_on;
    const bIsAddOn = (b as any).isAddOn || (b as any).is_add_on;
    
    if (aIsAddOn && !bIsAddOn) return 1; // a is add-on, b is not - a goes last
    if (!aIsAddOn && bIsAddOn) return -1; // a is not add-on, b is - b goes last
    return 0; // Both same type, maintain original order
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsCategoryDropdownOpen(false);
      }
    };

    if (isCategoryDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCategoryDropdownOpen]);

  // Get today's hours
  const today = new Date().getDay();
  const todayHours = operatingHours.find(h => h.dayOfWeek === today);

  const makeLocalYmd = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Some DB drivers return DATE columns as `Date` objects at UTC midnight. Using local getters can shift the day.
  const makeUtcYmd = (date: Date): string => {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const startOfLocalDay = (date: Date): Date => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  // Normalize anything we might get back from the API (DATE/TIMESTAMP/Date/string) into local YYYY-MM-DD.
  const toLocalYmd = (value: unknown): string | null => {
    if (!value) return null;

    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) return null;
      // Treat DATE values as date-only using UTC components to avoid timezone day-shift.
      return makeUtcYmd(value);
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      // If it's already ISO-ish, just take the YYYY-MM-DD portion.
      const isoPrefixMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
      if (isoPrefixMatch?.[1]) return isoPrefixMatch[1];

      // Fall back to Date parsing.
      const parsed = new Date(trimmed);
      if (Number.isNaN(parsed.getTime())) return null;
      return makeLocalYmd(parsed);
    }

    if (typeof value === 'number') {
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return null;
      return makeLocalYmd(parsed);
    }

    return null;
  };

  const localDateFromYmd = (ymd: string): Date | null => {
    const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return null;
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
    return startOfLocalDay(new Date(year, month - 1, day));
  };

  // Fetch today's closures
  useEffect(() => {
    const fetchTodayClosures = async () => {
      if (!business?.id) return;
      
      try {
        const todayStr = makeLocalYmd(new Date());
        const closures = await apiService.getBusinessClosuresForDateRange(business.id, todayStr, todayStr);
        setTodayClosures(closures || []);
      } catch (error) {
        setTodayClosures([]);
      }
    };

    fetchTodayClosures();
  }, [business?.id]);

  // Smart function to generate contextual message for today's hours
  const getTodayHoursMessage = (): string => {
    if (!todayHours) {
      return 'Hours not available';
    }

    const todayLocal = startOfLocalDay(new Date());
    const todayDayOfWeek = todayLocal.getDay(); // 0-6 (Sunday-Saturday)
    const todayStr = makeLocalYmd(todayLocal);
    const todayYear = todayLocal.getFullYear();

    // Check for closures that apply to today
    const applicableClosures = todayClosures.filter(closure => {
      try {
        if (!closure) return false;

        const startYmd = toLocalYmd((closure as any).startDate);
        const endYmd = toLocalYmd((closure as any).endDate);
        if (!startYmd || !endYmd) return false;

        const repeatYearly =
          (closure as any).repeatYearly === true ||
          (closure as any).repeatYearly === 1 ||
          (closure as any).repeatYearly === '1' ||
          (closure as any).repeatYearly === 'true';

        if (repeatYearly) {
          const start = localDateFromYmd(`${todayYear}-${startYmd.slice(5, 7)}-${startYmd.slice(8, 10)}`);
          const end = localDateFromYmd(`${todayYear}-${endYmd.slice(5, 7)}-${endYmd.slice(8, 10)}`);
          if (!start || !end) return false;

          // Handle ranges that wrap around the end of the year (e.g., Dec 30 → Jan 02)
          if (end.getTime() < start.getTime()) {
            return todayLocal.getTime() >= start.getTime() || todayLocal.getTime() <= end.getTime();
          }

          return todayLocal.getTime() >= start.getTime() && todayLocal.getTime() <= end.getTime();
        }

        const startDate = localDateFromYmd(startYmd);
        const endDate = localDateFromYmd(endYmd);
        if (!startDate || !endDate) return false;

        return todayLocal.getTime() >= startDate.getTime() && todayLocal.getTime() <= endDate.getTime();
      } catch (error) {
        return false;
      }
    });

    // Check for all-day closures first (before any other checks)
    const allDayClosures = applicableClosures.filter(c => {
      // All-day closure: closureType is 'all_day', or no explicit start/end time provided.
      const startTimeStr = c.startTime ? String(c.startTime).trim() : '';
      const endTimeStr = c.endTime ? String(c.endTime).trim() : '';
      const hasNoTime = !startTimeStr && !endTimeStr;
      const isAllDayType = (c as any).closureType === 'all_day';
      return isAllDayType || hasNoTime;
    });
    
    if (allDayClosures.length > 0) {
      return 'Business is closed today';
    }

    // Check if closed in operating hours
    if (todayHours.isClosed) {
      return 'Business is closed today';
    }

    // Parse times
    const parseTime = (timeStr: string): number => {
      if (!timeStr) return 0;
      const parts = timeStr.split(':');
      const hours = parseInt(parts[0], 10);
      const minutes = parts[1] ? parseInt(parts[1], 10) : 0;
      return hours * 60 + minutes; // Convert to minutes since midnight
    };

    const openMinutes = parseTime(todayHours.openTime);
    const closeMinutes = parseTime(todayHours.closeTime);
    const openHour = Math.floor(openMinutes / 60);
    const closeHour = Math.floor(closeMinutes / 60);
    const openMinute = openMinutes % 60;
    const closeMinute = closeMinutes % 60;
    const durationHours = (closeMinutes - openMinutes) / 60;

    // Check for time-range closures (closures during operating hours)
    const timeRangeClosures = applicableClosures
      .filter(c => (c as any).closureType === 'time_range')
      .filter(c => {
        const hasStartTime = c.startTime && String(c.startTime).trim() !== '';
        const hasEndTime = c.endTime && String(c.endTime).trim() !== '';
        return hasStartTime && hasEndTime;
      })
      .sort((a, b) => String(a.startTime).localeCompare(String(b.startTime)));

    if (timeRangeClosures.length > 0) {
      const ranges = timeRangeClosures.map(c => ({
        start: formatTime(String(c.startTime)),
        end: formatTime(String(c.endTime)),
        startHour: Math.floor(parseTime(String(c.startTime)) / 60)
      }));

      // If there's exactly one midday closure, call it out as lunch.
      if (ranges.length === 1 && ranges[0].startHour >= 11 && ranges[0].startHour <= 14) {
        return `Open today, closed for lunch ${ranges[0].start}–${ranges[0].end}`;
      }

      const rangesText = ranges.map(r => `${r.start}–${r.end}`).join(', ');
      return `Open today, with closures ${rangesText}`;
    }

    // Very late hours (after 9 PM / 21:00)
    if (closeHour >= 21) {
      return 'Open until very late this evening';
    }

    // Late hours (after 8 PM / 20:00)
    if (closeHour >= 20) {
      if (openHour >= 12) {
        return 'Open until late evening today';
      }
      return 'We stay open late into the evening today';
    }

    // Very early hours (before 6 AM / 06:00)
    if (openHour < 6) {
      return 'We open very early this morning';
    }

    // Early hours (before 8 AM / 08:00)
    if (openHour < 8) {
      if (closeHour < 12) {
        return 'Early morning hours today';
      }
      return 'We open early this morning';
    }

    // Short hours (less than 5 hours)
    if (durationHours < 5) {
      return 'Limited hours today';
    }

    // Very short hours (less than 4 hours)
    if (durationHours < 4) {
      return 'Short hours today';
    }

    // Long hours (more than 12 hours)
    if (durationHours > 12) {
      return 'Extended hours available today';
    }

    // Long hours (more than 10 hours)
    if (durationHours > 10) {
      return 'Open all day with extended hours';
    }

    // Weekend patterns
    if (todayDayOfWeek === 0 || todayDayOfWeek === 6) { // Sunday or Saturday
      if (openHour >= 10 && closeHour <= 18) {
        return 'Weekend hours in effect';
      }
      if (closeHour >= 20) {
        return 'Open late this weekend';
      }
    }

    // Weekday patterns
    if (todayDayOfWeek >= 1 && todayDayOfWeek <= 5) { // Monday to Friday
      if (openHour >= 9 && openHour <= 10 && closeHour >= 17 && closeHour <= 18) {
        return 'Standard business hours today';
      }
      if (closeHour >= 20) {
        return 'Open late on weekdays';
      }
    }

    // Afternoon-only hours (opens after 12 PM)
    if (openHour >= 12) {
      return 'Open this afternoon and evening';
    }

    // Morning-focused hours (closes before 2 PM)
    if (closeHour < 14) {
      return 'Morning hours today';
    }

    // Default friendly message
    return 'Open for your convenience today';
  };


  // Check if user has upcoming appointments for this business
  useEffect(() => {
    const checkAppointments = async () => {
      if (!user || !business?.id) {
        setHasUpcomingAppointments(false);
        return;
      }

      try {
        setCheckingAppointments(true);
        const response = await apiService.getCustomerAppointments({
          businessId: business.id,
          status: 'upcoming',
          limit: 1
        });
        setHasUpcomingAppointments((response.data?.length || 0) > 0);
      } catch (error) {
        console.error('Error checking appointments:', error);
        setHasUpcomingAppointments(false);
      } finally {
        setCheckingAppointments(false);
      }
    };

    checkAppointments();
  }, [user, business?.id]);

  // Fetch favorite services when user is logged in
  useEffect(() => {
    const fetchFavorites = async () => {
      if (!user || !business?.id) {
        setFavoriteServiceIds(new Set());
        setLoadingFavorites(false);
        setHasInitializedCategory(false); // Reset to allow initialization
        return;
      }

      try {
        setLoadingFavorites(true);
        setHasInitializedCategory(false); // Reset before loading
        const favorites = await apiService.getFavoriteServices('customer', business.id);
        const favoriteIds = new Set(favorites.map((f: Service) => f.id));
        setFavoriteServiceIds(favoriteIds);
      } catch (error) {
        console.error('Error fetching favorite services:', error);
        // Don't show error toast for unauthenticated users
        if (user) {
          toast.error('Failed to load favorite services');
        }
      } finally {
        setLoadingFavorites(false);
      }
    };

    fetchFavorites();
  }, [user, business?.id]);

  // Handle favorite toggle
  const handleFavoriteToggle = async (e: React.MouseEvent, serviceId: string) => {
    e.stopPropagation(); // Prevent service booking click when clicking favorite button

    if (!user) {
      toast.error('Please sign in to save favorites');
      return;
    }

    const isCurrentlyFavorited = favoriteServiceIds.has(serviceId);
    const newFavoriteIds = new Set(favoriteServiceIds);

    // Optimistic update
    if (isCurrentlyFavorited) {
      newFavoriteIds.delete(serviceId);
    } else {
      newFavoriteIds.add(serviceId);
    }
    setFavoriteServiceIds(newFavoriteIds);

    try {
      if (isCurrentlyFavorited) {
        await apiService.removeFavoriteService(serviceId, 'customer', business?.id);
        toast.success('Removed from favorites');
      } else {
        await apiService.addFavoriteService(serviceId, 'customer', business?.id);
        toast.success('Added to favorites');
      }
    } catch (error: any) {
      // Revert optimistic update on error
      setFavoriteServiceIds(favoriteServiceIds);
      
      if (error.response?.status === 401) {
        toast.error('Please sign in to save favorites');
      } else if (error.response?.status === 409) {
        // Already favorited, just update the state
        setFavoriteServiceIds(newFavoriteIds);
      } else {
        toast.error(isCurrentlyFavorited ? 'Failed to remove from favorites' : 'Failed to add to favorites');
      }
    }
  };

  return (
    <div 
      className="min-h-screen"
      style={{
        backgroundColor: bgColor,
        color: textColor,
        fontFamily: branding?.fontFamily || 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}
    >
      <style>{`
        :root {
          --brand: ${brandColor};
          --brand-dark: ${brandDark};
          --brand-soft: ${brandSoft};
          --bg: ${bgColor};
          --surface: ${surfaceColor};
          --border: ${borderColor};
          --text: ${textColor};
          --muted: ${mutedColor};
        }
      `}</style>

      {/* Header */}
      <BusinessHeader
        business={business}
        branding={branding}
        surfaceColor={surfaceColor}
        borderColor={borderColor}
        textColor={textColor}
        mutedColor={mutedColor}
        brandColor={brandColor}
        brandSoft={brandSoft}
        brandDark={brandDark}
        onBookingClick={handleBookingClick}
        showBookNowButton={true}
      />

      <main className="py-4">
        <div className="container mx-auto px-4">
          {/* Hero + Side Info */}
          <div className="flex flex-col lg:flex-row gap-4 items-stretch mb-4">
            <div className="lg:w-7/12">
              <section 
                className="rounded-2xl border p-7 shadow-lg h-full flex flex-col"
                style={{
                  backgroundColor: surfaceColor,
                  borderColor: borderColor
                }}
                aria-labelledby="hero-title"
              >
                <div>
                  <h1 
                    id="hero-title" 
                    className="text-3xl font-bold mb-3"
                    style={{ color: brandColor }}
                  >
                    {business.name}
                  </h1>
                  {business.description && (
                    <p 
                      className="text-base mb-2 whitespace-pre-line"
                      style={{ color: '#000000' }}
                    >
                      {business.description}
                    </p>
                  )}
                </div>

                {!user ? (
                  <div className="mt-auto">
                    <div className="mb-2 mt-4">
                      <button
                        type="button"
                        className="w-full md:w-auto md:inline-flex items-center justify-center mb-2 md:mb-0 md:mr-2 px-6 py-2 rounded-lg font-medium transition-colors hover:opacity-90"
                        style={{
                          backgroundColor: brandColor,
                          borderColor: brandColor,
                          color: '#fff',
                          boxShadow: `0 8px 18px ${brandColor}4d`
                        }}
                        onClick={handleBookingClick}
                      >
                        <i className="bi bi-calendar-check mr-2"></i>
                        Book Your Appointment
                      </button>
                      <button
                        type="button"
                        className="w-full md:w-auto md:inline-flex items-center justify-center px-6 py-2 rounded-lg font-medium border transition-opacity hover:opacity-90"
                        style={{
                          backgroundColor: brandSoft,
                          borderColor: brandColor,
                          color: brandColor
                        }}
                        onClick={() => {
                          const redirectUrl = `/${business.shortName}`;
                          navigate(`/register?type=customer&redirect=${encodeURIComponent(redirectUrl)}`);
                        }}
                      >
                        <i className="bi bi-person-plus mr-2"></i>
                        Sign up
                      </button>
                    </div>
                    <p className="text-sm mb-0" style={{ color: mutedColor }}>
                      Already a customer?{' '}
                      <button
                        className="p-0 align-baseline underline bg-transparent border-none cursor-pointer"
                        style={{ color: brandColor }}
                        onClick={() => {
                          const redirectUrl = `/${business.shortName}`;
                          navigate(`/login?type=customer&redirect=${encodeURIComponent(redirectUrl)}`);
                        }}
                      >
                        Sign in
                      </button>
                      {' '}to view your visits.
                    </p>
                    {(business.website || business.facebookUrl || business.instagramUrl || business.youtubeUrl) && (
                      <div className="mt-4 pt-4 border-t flex items-center justify-end gap-3" style={{ borderColor: borderColor }}>
                        <p 
                          className="text-sm mb-0"
                          style={{ color: mutedColor }}
                        >
                          Connect with us
                        </p>
                        <div className="flex items-center gap-3">
                          {business.website && (
                            <a
                              href={business.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center transition-colors hover:opacity-80"
                              style={{ color: brandColor }}
                              title="Website"
                            >
                              <i className="bi bi-globe text-lg"></i>
                            </a>
                          )}
                          {business.facebookUrl && (
                            <a
                              href={business.facebookUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center transition-colors hover:opacity-80"
                              style={{ color: brandColor }}
                              title="Facebook"
                            >
                              <i className="bi bi-facebook text-lg"></i>
                            </a>
                          )}
                          {business.instagramUrl && (
                            <a
                              href={business.instagramUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center transition-colors hover:opacity-80"
                              style={{ color: brandColor }}
                              title="Instagram"
                            >
                              <i className="bi bi-instagram text-lg"></i>
                            </a>
                          )}
                          {business.youtubeUrl && (
                            <a
                              href={business.youtubeUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center transition-colors hover:opacity-80"
                              style={{ color: brandColor }}
                              title="YouTube"
                            >
                              <i className="bi bi-youtube text-lg"></i>
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mt-auto">
                    <div className="mb-2 mt-4">
                      <button
                        type="button"
                        className="w-full md:w-auto md:inline-flex items-center justify-center mb-2 md:mb-0 md:mr-2 px-6 py-2 rounded-lg font-medium transition-colors hover:opacity-90"
                        style={{
                          backgroundColor: brandColor,
                          borderColor: brandColor,
                          color: '#fff',
                          boxShadow: `0 8px 18px ${brandColor}4d`
                        }}
                        onClick={handleBookingClick}
                      >
                        <i className="bi bi-calendar-plus mr-2"></i>
                        Book a New Appointment
                      </button>
                    </div>
                    {(business.website || business.facebookUrl || business.instagramUrl || business.youtubeUrl) && (
                      <div className="mt-4 pt-4 border-t flex items-center justify-end gap-3" style={{ borderColor: borderColor }}>
                        <p 
                          className="text-sm mb-0"
                          style={{ color: mutedColor }}
                        >
                          Connect with us
                        </p>
                        <div className="flex items-center gap-3">
                          {business.website && (
                            <a
                              href={business.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center transition-colors hover:opacity-80"
                              style={{ color: brandColor }}
                              title="Website"
                            >
                              <i className="bi bi-globe text-lg"></i>
                            </a>
                          )}
                          {business.facebookUrl && (
                            <a
                              href={business.facebookUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center transition-colors hover:opacity-80"
                              style={{ color: brandColor }}
                              title="Facebook"
                            >
                              <i className="bi bi-facebook text-lg"></i>
                            </a>
                          )}
                          {business.instagramUrl && (
                            <a
                              href={business.instagramUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center transition-colors hover:opacity-80"
                              style={{ color: brandColor }}
                              title="Instagram"
                            >
                              <i className="bi bi-instagram text-lg"></i>
                            </a>
                          )}
                          {business.youtubeUrl && (
                            <a
                              href={business.youtubeUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center transition-colors hover:opacity-80"
                              style={{ color: brandColor }}
                              title="YouTube"
                            >
                              <i className="bi bi-youtube text-lg"></i>
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </section>
            </div>

            <div className="lg:w-5/12">
              <div className="flex flex-col gap-3 h-full">
                {/* Contact Info */}
                <section 
                  className="rounded-xl border p-5 shadow-md"
                  style={{
                    backgroundColor: surfaceColor,
                    borderColor: borderColor
                  }}
                  aria-label="Contact information"
                >
                  <p 
                    className="text-xs uppercase tracking-widest mb-2"
                    style={{ color: mutedColor }}
                  >
                    Visit us
                  </p>
                  {business.address && (
                    <div className="flex mb-2">
                      <i className="bi bi-geo-alt mr-2 mt-1" style={{ color: brandColor }}></i>
                      <div>
                        <div className="font-semibold" style={{ color: textColor }}>Address</div>
                        <div style={{ color: textColor }}>{business.address}</div>
                        <a
                          className="inline-flex items-center mt-1"
                          target="_blank"
                          rel="noopener"
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(business.address)}`}
                          style={{ color: brandColor }}
                        >
                          <i className="bi bi-arrow-right-short mr-1"></i>
                          <span>Get directions</span>
                        </a>
                      </div>
                    </div>
                  )}
                  {business.phone && (
                    <div className="flex">
                      <i className="bi bi-telephone mr-2 mt-1" style={{ color: brandColor }}></i>
                      <div>
                        <div className="font-semibold" style={{ color: textColor }}>Phone</div>
                        <a 
                          href={`tel:${business.phone}`}
                          style={{ color: brandColor }}
                        >
                          {business.phone}
                        </a>
                      </div>
                    </div>
                  )}
                </section>

                {/* Today's Hours */}
                <section 
                  className="rounded-xl border p-5 shadow-md flex-1 flex flex-col"
                  style={{
                    backgroundColor: surfaceColor,
                    borderColor: borderColor
                  }}
                  aria-label="Today's hours"
                >
                  <p 
                    className="text-xs uppercase tracking-widest mb-2"
                    style={{ color: mutedColor }}
                  >
                    Today's hours
                  </p>
                  <p className="mb-1 text-sm" style={{ color: mutedColor }}>
                    {getTodayHoursMessage()}
                  </p>
                  <p className="font-semibold mb-0" style={{ color: textColor }}>
                    {todayHours && !todayHours.isClosed
                      ? `${formatTime(todayHours.openTime)} – ${formatTime(todayHours.closeTime)}`
                      : 'Closed'}
                  </p>
                </section>

              </div>
            </div>
          </div>

          {/* Photo Gallery */}
          {galleryImages.length > 0 && (
            <section className="mb-4" aria-labelledby="gallery-heading">
              <div className="flex justify-between items-end flex-wrap mb-2">
                <div>
                  <h2 
                    id="gallery-heading" 
                    className="text-2xl mb-0"
                    style={{ color: textColor }}
                  >
                    Photo Gallery
                  </h2>
                  <p 
                    className="text-sm mb-0"
                    style={{ color: mutedColor }}
                  >
                    Explore our work and space
                  </p>
                </div>
                <div className="flex gap-2 items-center md:flex-row flex-row w-full md:w-auto">
                  <div
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 text-sm font-medium cursor-default flex-1 md:flex-none"
                    style={{ color: textColor }}
                  >
                    <Camera className="w-4 h-4" />
                    <span>{galleryImages.length} {galleryImages.length === 1 ? 'photo' : 'photos'}</span>
                  </div>
                  <button
                    onClick={() => setIsGalleryModalOpen(true)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors text-sm font-medium flex-1 md:flex-none"
                    style={{ color: textColor }}
                  >
                    <Maximize2 className="w-4 h-4" />
                    <span>Tap to zoom</span>
                  </button>
                </div>
              </div>

              <div 
                className="rounded-2xl border overflow-hidden shadow-md relative"
                style={{
                  backgroundColor: surfaceColor,
                  borderColor: borderColor
                }}
              >
                {/* Carousel Container */}
                <div className="relative">
                  {/* Images */}
                  <div className="relative overflow-hidden" style={{ aspectRatio: '16/9' }}>
                    <div 
                      className="flex transition-transform duration-500 ease-in-out h-full"
                      style={{ transform: `translateX(-${currentGalleryIndex * 100}%)` }}
                    >
                      {galleryImages.map((image, index) => (
                        <div 
                          key={index}
                          className="w-full flex-shrink-0 h-full"
                        >
                          <img
                            src={image}
                            alt={`Gallery image ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Navigation Arrows */}
                  {galleryImages.length > 1 && (
                    <>
                      <button
                        onClick={() => setCurrentGalleryIndex((prev) => 
                          prev === 0 ? galleryImages.length - 1 : prev - 1
                        )}
                        className="absolute left-4 top-1/2 -translate-y-1/2 transition-opacity hover:opacity-80 z-10"
                        aria-label="Previous image"
                      >
                        <ChevronLeft className="w-10 h-10 text-white drop-shadow-lg" strokeWidth={1.5} />
                      </button>
                      <button
                        onClick={() => setCurrentGalleryIndex((prev) => 
                          prev === galleryImages.length - 1 ? 0 : prev + 1
                        )}
                        className="absolute right-4 top-1/2 -translate-y-1/2 transition-opacity hover:opacity-80 z-10"
                        aria-label="Next image"
                      >
                        <ChevronRight className="w-10 h-10 text-white drop-shadow-lg" strokeWidth={1.5} />
                      </button>
                    </>
                  )}

                  {/* Dots Indicator */}
                  {galleryImages.length > 1 && (
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                      {galleryImages.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentGalleryIndex(index)}
                          className={`w-2 h-2 rounded-full transition-all ${
                            index === currentGalleryIndex ? 'w-8' : ''
                          }`}
                          style={{
                            backgroundColor: index === currentGalleryIndex 
                              ? brandColor 
                              : 'rgba(255, 255, 255, 0.5)'
                          }}
                          aria-label={`Go to image ${index + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Thumbnail Navigation */}
                {galleryImages.length > 1 && (
                  <div className="p-4 bg-gray-50">
                    <style>{`
                      .thumbnail-scrollbar::-webkit-scrollbar {
                        height: 6px;
                      }
                      .thumbnail-scrollbar::-webkit-scrollbar-track {
                        background: transparent;
                      }
                      .thumbnail-scrollbar::-webkit-scrollbar-thumb {
                        background: ${brandColor}40;
                        border-radius: 3px;
                      }
                      .thumbnail-scrollbar::-webkit-scrollbar-thumb:hover {
                        background: ${brandColor}60;
                      }
                    `}</style>
                    <div 
                      ref={thumbnailContainerRef}
                      className={`thumbnail-scrollbar flex gap-2 overflow-x-auto pb-2 snap-x snap-mandatory scroll-smooth cursor-grab active:cursor-grabbing ${shouldCenterThumbnails ? 'justify-center' : ''}`}
                      style={{ 
                        scrollbarWidth: 'thin',
                        scrollbarColor: `${brandColor}40 transparent`
                      }}
                      onMouseDown={(e) => {
                        if (thumbnailContainerRef.current) {
                          setIsThumbnailDragging(false);
                          setThumbnailDragDistance(0);
                          setThumbnailStartX(e.pageX - thumbnailContainerRef.current.offsetLeft);
                          setThumbnailScrollLeft(thumbnailContainerRef.current.scrollLeft);
                        }
                      }}
                      onMouseLeave={() => {
                        setIsThumbnailDragging(false);
                        setThumbnailDragDistance(0);
                      }}
                      onMouseUp={() => {
                        setIsThumbnailDragging(false);
                        setThumbnailDragDistance(0);
                      }}
                      onMouseMove={(e) => {
                        if (!thumbnailContainerRef.current) return;
                        const x = e.pageX - thumbnailContainerRef.current.offsetLeft;
                        const walk = Math.abs(x - thumbnailStartX);
                        
                        // Only start dragging if moved more than 5px
                        if (walk > 5) {
                          setIsThumbnailDragging(true);
                          setThumbnailDragDistance(walk);
                          e.preventDefault();
                          const scrollWalk = (x - thumbnailStartX) * 2; // Scroll speed multiplier
                          thumbnailContainerRef.current.scrollLeft = thumbnailScrollLeft - scrollWalk;
                        }
                      }}
                    >
                      {galleryImages.map((image, index) => (
                        <button
                          key={index}
                          onClick={(e) => {
                            // Only navigate if we didn't drag (moved less than 5px)
                            if (thumbnailDragDistance < 5) {
                              setCurrentGalleryIndex(index);
                            }
                          }}
                          className={`flex-shrink-0 rounded-lg overflow-hidden transition-all snap-center ${
                            index === currentGalleryIndex 
                              ? '' 
                              : 'opacity-60 hover:opacity-100'
                          }`}
                          style={{
                            width: '80px',
                            height: '60px',
                            border: index === currentGalleryIndex ? `3px solid ${brandColor}` : '3px solid transparent',
                            boxShadow: index === currentGalleryIndex ? `0 0 0 2px ${brandColor}20` : 'none'
                          }}
                          aria-label={`View image ${index + 1}`}
                        >
                          <img
                            src={image}
                            alt={`Thumbnail ${index + 1}`}
                            className="w-full h-full object-cover select-none"
                            draggable={false}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Services */}
          <section className="mb-4" aria-labelledby="services-heading">
            <div className="flex justify-between items-end flex-wrap mb-2">
              <div>
                <h2 
                  id="services-heading" 
                  className="text-2xl mb-0"
                  style={{ color: textColor }}
                >
                  Our Services
                </h2>
                <p 
                  className="text-sm mb-0"
                  style={{ color: mutedColor }}
                >
                  Browse by category. Prices and times are shown before you book.
                </p>
              </div>
            </div>

            <div 
              className="rounded-2xl border p-5 shadow-md"
              style={{
                backgroundColor: surfaceColor,
                borderColor: borderColor
              }}
            >
              <div className="flex flex-col lg:flex-row lg:items-stretch lg:gap-8">
                {/* Category Filter - Mobile Dropdown / Desktop Vertical Column */}
                {bookableServices.length > 0 && categories.length > 0 && (
                  <>
                    {/* Mobile/Tablet Dropdown */}
                    <div className="lg:hidden mb-6">
                    <div className="flex flex-col gap-2">
                      <div className="relative w-full" ref={dropdownRef}>
                        <button
                          onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                          className="w-full px-5 py-2.5 text-sm font-medium rounded-md transition-opacity hover:opacity-90 flex items-center justify-between bg-white border border-gray-300 text-gray-700 h-10"
                          style={selectedCategory !== 'all' ? { borderColor: brandColor } : {}}
                        >
                          <div className="flex items-center gap-2">
                            {(() => {
                              const selectedCat = categoriesWithFavorites.find(c => c.id === selectedCategory || c.name === selectedCategory);
                              if (selectedCategory === 'all') {
                                return <List className="w-4 h-4" />;
                              } else if (selectedCat?.icon) {
                                return <span className="text-base">{selectedCat.icon}</span>;
                              } else {
                                return <Tag className="w-4 h-4" />;
                              }
                            })()}
                            <span>
                              {(() => {
                                const selectedCat = categoriesWithFavorites.find(c => c.id === selectedCategory || c.name === selectedCategory);
                                return selectedCat?.name || 'All Services';
                              })()}
                            </span>
                          </div>
                          <ChevronDown 
                            className={`w-4 h-4 transition-transform ${isCategoryDropdownOpen ? 'transform rotate-180' : ''}`}
                          />
                        </button>
                        {isCategoryDropdownOpen && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                            <div
                              className={`px-4 py-2.5 text-sm font-medium cursor-pointer flex items-center gap-2 ${
                                selectedCategory === 'all'
                                  ? 'text-white'
                                  : 'text-gray-700 hover:bg-gray-50'
                              }`}
                              style={selectedCategory === 'all' ? { backgroundColor: brandColor } : {}}
                              onClick={() => {
                                setSelectedCategory('all');
                                setIsCategoryDropdownOpen(false);
                              }}
                            >
                              <List className="w-4 h-4" />
                              <span>All Services</span>
                            </div>
                            {categoriesWithFavorites.slice(1).map((category) => (
                              <div
                                key={category.id}
                                className={`px-4 py-2.5 text-sm font-medium cursor-pointer flex items-center gap-2 ${
                                  (selectedCategory === category.id || selectedCategory === category.name)
                                    ? 'text-white'
                                    : 'text-gray-700 hover:bg-gray-50'
                                }`}
                                style={(selectedCategory === category.id || selectedCategory === category.name) ? { backgroundColor: brandColor } : {}}
                                onClick={() => {
                                  setSelectedCategory(category.id);
                                  setIsCategoryDropdownOpen(false);
                                }}
                              >
                                {category.icon ? (
                                  <span className="text-base">{category.icon}</span>
                                ) : (
                                  <Tag className="w-4 h-4" />
                                )}
                                <span>{category.name}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {/* Search Bar - Mobile */}
                      <div className="relative w-full">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search services..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-md focus:outline-none text-sm font-medium bg-white text-gray-700 transition-all h-10"
                          style={{ 
                            '--tw-ring-color': brandColor,
                          } as React.CSSProperties & { '--tw-ring-color': string }}
                          onFocus={(e) => {
                            e.target.style.borderColor = brandColor;
                            e.target.style.boxShadow = `0 0 0 2px ${brandColor}20`;
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#D1D5DB';
                            e.target.style.boxShadow = '';
                          }}
                        />
                        {searchQuery && (
                          <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                            type="button"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Desktop Vertical Column */}
                  <div className="hidden lg:flex lg:flex-col lg:w-48 lg:flex-shrink-0 lg:gap-2">
                    <button
                      onClick={() => setSelectedCategory('all')}
                      className={`px-4 py-2.5 text-sm font-medium rounded-lg transition-all flex items-center gap-2 w-full ${
                        selectedCategory === 'all'
                          ? 'text-white shadow-sm'
                          : 'text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                      }`}
                      style={selectedCategory === 'all' ? { backgroundColor: brandColor } : {}}
                    >
                      <List className="w-4 h-4 flex-shrink-0" />
                      <span>All Services</span>
                    </button>
                    {categoriesWithFavorites.slice(1).map((category) => (
                      <button
                        key={category.id}
                        onClick={() => setSelectedCategory(category.id)}
                        className={`px-4 py-2.5 text-sm font-medium rounded-lg transition-all flex items-center gap-2 w-full ${
                          (selectedCategory === category.id || selectedCategory === category.name)
                            ? 'text-white shadow-sm'
                            : 'text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                        }`}
                        style={(selectedCategory === category.id || selectedCategory === category.name) ? { backgroundColor: brandColor } : {}}
                      >
                        {category.icon ? (
                          <span className="text-base flex items-center justify-center flex-shrink-0">{category.icon}</span>
                        ) : (
                          <Tag className="w-4 h-4 flex-shrink-0" />
                        )}
                        <span>{category.name}</span>
                      </button>
                    ))}
                  </div>
                </>
                )}

                {/* Services Grid */}
                <div className="flex-1 space-y-6">
                  {/* Desktop Search Bar */}
                  <div className="hidden lg:block mb-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search services..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-md focus:outline-none text-sm font-medium bg-white text-gray-700 transition-all h-10"
                        style={{ 
                          '--tw-ring-color': brandColor,
                        } as React.CSSProperties & { '--tw-ring-color': string }}
                        onFocus={(e) => {
                          e.target.style.borderColor = brandColor;
                          e.target.style.boxShadow = `0 0 0 2px ${brandColor}20`;
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#D1D5DB';
                          e.target.style.boxShadow = '';
                        }}
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                          type="button"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div className="lg:pr-0">
                    {bookableServices.length === 0 ? (
                      <div className="text-center py-16">
                        <p className="text-gray-500 text-lg">No services available at the moment.</p>
                      </div>
                    ) : filteredServices.length === 0 ? (
                      <div className="text-center py-16">
                        <p className="text-gray-500 text-lg">No services found in this category.</p>
                      </div>
                    ) : (
                      <>
                        {/* Mobile: Service count divider */}
                        <div className="lg:hidden mb-3 pb-2 border-b border-gray-200 -mt-4 lg:mt-0">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-6 h-6 rounded-md flex items-center justify-center"
                                style={{ backgroundColor: `${brandColor}15` }}
                              >
                                <List className="w-3.5 h-3.5" style={{ color: brandColor }} />
                              </div>
                              <span className="text-sm font-semibold" style={{ color: textColor }}>
                                {filteredServices.length} {filteredServices.length === 1 ? 'Service' : 'Services'}
                              </span>
                            </div>
                            {searchQuery && (
                              <span className="text-xs" style={{ color: mutedColor }}>
                                Matching "{searchQuery}"
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Mobile: Limited services */}
                        <div className="lg:hidden grid grid-cols-1 gap-4 md:gap-6" aria-label="Service list">
                          {filteredServices.slice(0, mobileServicesDisplayCount).map((service) => {
                          const isFavorited = favoriteServiceIds.has(service.id);

                          return (
                          <div
                            key={service.id}
                            onClick={() => handleServiceBookingClick(service.id)}
                            className="border rounded-lg shadow-sm hover:shadow-md transition-all cursor-pointer px-3 pt-3 pb-3 flex flex-col overflow-visible border-gray-200 bg-white relative"
                          >
                            {/* Favorite Button - Only show when user is logged in */}
                            {user && (
                              <button
                                onClick={(e) => handleFavoriteToggle(e, service.id)}
                                className={`absolute top-2 right-2 p-1.5 rounded-full transition-all z-10 ${
                                  isFavorited 
                                    ? 'text-red-500 hover:text-red-600 bg-red-50 hover:bg-red-100' 
                                    : 'text-gray-400 hover:text-red-500 bg-white hover:bg-gray-50'
                                }`}
                                aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                                title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                              >
                                <Heart 
                                  className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`}
                                />
                              </button>
                            )}

                            {/* Service Icon and Name */}
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0">
                                <div 
                                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                                  style={{ backgroundColor: `${brandColor}15` }}
                                >
                                  {(() => {
                                    const icon = getServiceIcon(service);
                                    return icon ? (
                                      <span className="text-xl" style={{ color: brandColor }}>
                                        {icon}
                                      </span>
                                    ) : (
                                      <i 
                                        className="bi bi-star-fill"
                                        style={{ 
                                          fontSize: '20px',
                                          color: '#FBBF24'
                                        }}
                                      ></i>
                                    );
                                  })()}
                                </div>
                              </div>
                              
                              <div className="flex-1 min-w-0 overflow-hidden">
                                <h3 
                                  className="text-sm font-semibold mb-1.5 truncate"
                                  style={{ color: textColor }}
                                >
                                  {service.name}
                                </h3>
                                {/* Duration and Price under service name */}
                                <div className="flex items-center space-x-2 text-xs" style={{ color: mutedColor }}>
                                  <div className="flex items-center space-x-1 flex-shrink-0">
                                    <Clock className="w-3 h-3 flex-shrink-0" />
                                    <span className="whitespace-nowrap">{formatDuration(service.durationMinutes)}</span>
                                  </div>
                                  <span 
                                    className="font-semibold whitespace-nowrap flex-shrink-0"
                                    style={{ color: brandColor }}
                                  >
                                    {(service as any)?.quotable === true
                                      ? 'Request a Quote'
                                      : formatPrice(Number((service as any)?.price ?? 0))}
                                  </span>
                                  {/* Add-on Badge - Next to Price */}
                                  {((service as any).isAddOn || (service as any).is_add_on) && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-sm ml-1">
                                      Add-on
                                    </span>
                                  )}
                                </div>
                              </div>
                              
                              {/* Service Location Icon - Bottom Right */}
                              {(() => {
                                const rawServiceLocation = service.serviceLocation || (service as any).service_location;
                                const serviceLocation = rawServiceLocation 
                                  ? (typeof rawServiceLocation === 'string' ? rawServiceLocation.toUpperCase().trim() : 'IN_STORE')
                                  : 'IN_STORE';
                                
                                if (serviceLocation === 'IN_STORE' || serviceLocation === 'INSTORE' || serviceLocation === 'ON_SITE' || serviceLocation === 'ONSITE') {
                                  const tooltipText = serviceLocation === 'IN_STORE' || serviceLocation === 'INSTORE' ? 'In-store service' : 'On-site service';
                                  return (
                                    <div className="absolute bottom-2 right-2 group">
                                      <div className="p-1.5 text-gray-400 bg-white rounded-full shadow-sm cursor-help">
                                        {serviceLocation === 'IN_STORE' || serviceLocation === 'INSTORE' ? (
                                          <Store className="w-4 h-4" />
                                        ) : (
                                          <MapPin className="w-4 h-4" />
                                        )}
                                      </div>
                                      {/* Tooltip */}
                                      <div className="absolute right-0 bottom-full mb-2 hidden group-hover:block z-50 pointer-events-none whitespace-nowrap">
                                        <div className="bg-gray-900 text-white text-xs rounded-lg py-1.5 px-2 shadow-lg">
                                          {tooltipText}
                                          <div className="absolute right-2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          </div>
                          );
                        })}
                        </div>
                        
                        {/* Desktop: All services */}
                        <div className="hidden lg:grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6" aria-label="Service list">
                          {filteredServices.map((service) => {
                            const isFavorited = favoriteServiceIds.has(service.id);

                            return (
                            <div
                              key={service.id}
                              onClick={() => handleServiceBookingClick(service.id)}
                              className="border rounded-lg shadow-sm hover:shadow-md transition-all cursor-pointer px-3 pt-3 pb-3 flex flex-col overflow-visible border-gray-200 bg-white relative"
                            >
                              {/* Favorite Button - Only show when user is logged in */}
                              {user && (
                                <button
                                  onClick={(e) => handleFavoriteToggle(e, service.id)}
                                  className={`absolute top-2 right-2 p-1.5 rounded-full transition-all z-10 ${
                                    isFavorited 
                                      ? 'text-red-500 hover:text-red-600 bg-red-50 hover:bg-red-100' 
                                      : 'text-gray-400 hover:text-red-500 bg-white hover:bg-gray-50'
                                  }`}
                                  aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                                  title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                                >
                                  <Heart 
                                    className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`}
                                  />
                                </button>
                              )}

                              {/* Service Icon and Name */}
                              <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0">
                                  <div 
                                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                                    style={{ backgroundColor: `${brandColor}15` }}
                                  >
                                    {(() => {
                                      const icon = getServiceIcon(service);
                                      return icon ? (
                                        <span className="text-xl" style={{ color: brandColor }}>
                                          {icon}
                                        </span>
                                      ) : (
                                        <i 
                                          className="bi bi-star-fill"
                                          style={{ 
                                            fontSize: '20px',
                                            color: '#FBBF24'
                                          }}
                                        ></i>
                                      );
                                    })()}
                                  </div>
                                </div>
                                
                                <div className="flex-1 min-w-0 overflow-hidden">
                                  <h3 
                                    className="text-sm font-semibold mb-1.5 truncate"
                                    style={{ color: textColor }}
                                  >
                                    {service.name}
                                  </h3>
                                  {/* Duration and Price under service name */}
                                  <div className="flex items-center space-x-2 text-xs" style={{ color: mutedColor }}>
                                    <div className="flex items-center space-x-1 flex-shrink-0">
                                      <Clock className="w-3 h-3 flex-shrink-0" />
                                      <span className="whitespace-nowrap">{formatDuration(service.durationMinutes)}</span>
                                    </div>
                                    <span 
                                      className="font-semibold whitespace-nowrap flex-shrink-0"
                                      style={{ color: brandColor }}
                                    >
                                      {(service as any)?.quotable === true
                                        ? 'Request a Quote'
                                        : formatPrice(Number((service as any)?.price ?? 0))}
                                    </span>
                                    {/* Add-on Badge - Next to Price */}
                                    {((service as any).isAddOn || (service as any).is_add_on) && (
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-sm ml-1">
                                        Add-on
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Service Location Icon - Bottom Right */}
                              {(() => {
                                const rawServiceLocation = service.serviceLocation || (service as any).service_location;
                                const serviceLocation = rawServiceLocation 
                                  ? (typeof rawServiceLocation === 'string' ? rawServiceLocation.toUpperCase().trim() : 'IN_STORE')
                                  : 'IN_STORE';
                                
                                if (serviceLocation === 'IN_STORE' || serviceLocation === 'INSTORE' || serviceLocation === 'ON_SITE' || serviceLocation === 'ONSITE') {
                                  const tooltipText = serviceLocation === 'IN_STORE' || serviceLocation === 'INSTORE' ? 'In-store service' : 'On-site service';
                                  return (
                                    <div className="absolute bottom-2 right-2 group">
                                      <div className="p-1.5 text-gray-400 bg-white rounded-full shadow-sm cursor-help">
                                        {serviceLocation === 'IN_STORE' || serviceLocation === 'INSTORE' ? (
                                          <Store className="w-4 h-4" />
                                        ) : (
                                          <MapPin className="w-4 h-4" />
                                        )}
                                      </div>
                                      {/* Tooltip */}
                                      <div className="absolute right-0 bottom-full mb-2 hidden group-hover:block z-50 pointer-events-none whitespace-nowrap">
                                        <div className="bg-gray-900 text-white text-xs rounded-lg py-1.5 px-2 shadow-lg">
                                          {tooltipText}
                                          <div className="absolute right-2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                            );
                          })}
                        </div>
                        
                        {/* Load More Button - Mobile Only */}
                        {filteredServices.length > mobileServicesDisplayCount && (
                          <div className="lg:hidden mt-6">
                            <button
                              onClick={() => setMobileServicesDisplayCount(prev => prev + 7)}
                              className="w-full px-6 py-3 text-sm font-medium rounded-lg transition-opacity hover:opacity-90 text-white shadow-lg"
                              style={{ 
                                backgroundColor: brandColor,
                                borderColor: brandColor
                              }}
                            >
                              Load More...
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Hours + Contact */}
          <section className="mb-4" aria-label="Hours and contact">
            <div className={`flex flex-col lg:flex-row gap-3 ${business.showContactForm === false ? 'lg:justify-center' : ''}`}>
              <div className={business.showContactForm === false ? 'lg:w-5/12' : 'lg:w-5/12'}>
                <section 
                  className="rounded-xl border p-5 shadow-md h-full"
                  style={{
                    backgroundColor: surfaceColor,
                    borderColor: borderColor
                  }}
                >
                  <p 
                    className="text-xs uppercase tracking-widest mb-2"
                    style={{ color: mutedColor }}
                  >
                    Operating hours
                  </p>
                  <ul className="list-none mb-0 text-sm" style={{ color: mutedColor }}>
                    {Array.from({ length: 7 }, (_, dayOfWeek) => {
                      const hours = operatingHours.find(h => h.dayOfWeek === dayOfWeek);
                      return (
                        <li key={dayOfWeek} className="flex justify-between py-1">
                          <span className="font-medium" style={{ color: textColor, minWidth: '4.1rem' }}>
                            {getDayName(dayOfWeek)}
                          </span>
                          <span style={{ color: textColor }}>
                            {!hours || hours.isClosed ? (
                              <span style={{ color: '#dc2626' }}>Closed</span>
                            ) : (
                              `${formatTime(hours.openTime)} – ${formatTime(hours.closeTime)}`
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              </div>

              {business.showContactForm !== false && (
                <div className="lg:w-7/12">
                  <section 
                    id="contact"
                    className="rounded-xl border p-5 shadow-md"
                    style={{
                      backgroundColor: surfaceColor,
                      borderColor: borderColor
                    }}
                  >
                    <ContactForm
                      businessName={business.name}
                      businessEmail={business.email}
                      businessId={business.id}
                      primaryColor={brandColor}
                      textColor={textColor}
                      showHeader={true}
                    buttonStyle={{
                      backgroundColor: brandColor,
                      color: '#fff'
                    }}
                    buttonClassName="btn-sm rounded-lg"
                    labelClassName="font-semibold"
                  />
                  </section>
                </div>
              )}
            </div>
          </section>
        </div>
      </main>

      {/* Gallery Zoom Modal */}
      {isGalleryModalOpen && galleryImages.length > 0 && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setIsGalleryModalOpen(false)}
        >
          <div 
            className="relative max-w-7xl w-full max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setIsGalleryModalOpen(false)}
              className="absolute top-4 right-4 z-10 bg-white/90 hover:bg-white rounded-full p-2 transition-all"
              aria-label="Close gallery"
            >
              <X className="w-6 h-6 text-gray-900" />
            </button>

            {/* Main Image */}
            <div className="relative overflow-hidden rounded-lg" style={{ aspectRatio: '16/9' }}>
              <img
                src={galleryImages[currentGalleryIndex]}
                alt={`Gallery image ${currentGalleryIndex + 1}`}
                className="w-full h-full object-contain"
              />
            </div>

            {/* Navigation Arrows */}
            {galleryImages.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentGalleryIndex((prev) => 
                      prev === 0 ? galleryImages.length - 1 : prev - 1
                    );
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 transition-opacity hover:opacity-80 z-10"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="w-10 h-10 text-white drop-shadow-lg" strokeWidth={1.5} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentGalleryIndex((prev) => 
                      prev === galleryImages.length - 1 ? 0 : prev + 1
                    );
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 transition-opacity hover:opacity-80 z-10"
                  aria-label="Next image"
                >
                  <ChevronRight className="w-10 h-10 text-white drop-shadow-lg" strokeWidth={1.5} />
                </button>
              </>
            )}

            {/* Image Counter */}
            {galleryImages.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full text-sm">
                {currentGalleryIndex + 1} / {galleryImages.length}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <BusinessFooter 
        business={business}
        branding={branding}
      />
      <TenantAskAiWidget
        shortName={business.shortName}
        businessName={business.name}
        enabled={business.askAiEnabled}
        showContactHint={business.showContactForm !== false}
        accentColor={brandColor}
        textColor={textColor}
      />
    </div>
  );
};

export default UnifiedLayout;

